import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAuthLocalStorage } from '../../../storage/localStorage';
import { AuthApiService } from '../../../service/authApiService';
import Logo from '../../../assets/images/common/comimport.png';
import LogoIcon from '../../../assets/images/common/importerr-favicon.png';
import {
  FaTachometerAlt, FaShoppingCart, FaBox, FaWallet,
  FaStore, FaCog, FaFileInvoice, FaChevronDown, FaChevronLeft,
  FaChevronRight, FaTimes, FaSignOutAlt, FaUser,
  FaSearch, FaUsers, FaExchangeAlt, FaChartBar, FaListAlt,
  FaTag, FaStar, FaGlobe, FaWarehouse, FaTicketAlt, FaBan,
  FaLayerGroup, FaUserFriends, FaClipboardList, FaDatabase,
  FaClock, FaUserShield, FaUpload, FaCalculator, FaTruck,
  FaCheckCircle, FaEnvelope
} from 'react-icons/fa';
import './sellerSidebar.css';

// Icon mapping for admin menus
const adminIconMap = {
  'dashboard': FaTachometerAlt,
  'search-log': FaSearch,
  'user-management': FaUsers,
  'transactions': FaExchangeAlt,
  'user-statistics': FaChartBar,
  'categories': FaListAlt,
  'tags': FaTag,
  'featured-categories': FaStar,
  'order-management': FaShoppingCart,
  'cross-border-orders': FaGlobe,
  'warehouse-management': FaWarehouse,
  'ticket-management': FaTicketAlt,
  'failed-orders': FaBan,
  'subscription': FaLayerGroup,
  'products': FaBox,
  'product-list': FaBox,
  'product-drafts': FaBox,
  'membership-management': FaUserFriends,
  'alibaba-product': FaGlobe,
  'sku-upload': FaUpload,
  'pricing-algorithms': FaCalculator,
  'logistics': FaTruck,
  'category-approval': FaCheckCircle,
  'inquiries': FaEnvelope,
  'internal-users': FaUserShield,
  'supplier-data-info': FaDatabase,
  'cron-logs': FaClock,
  'roles': FaUserShield,
  'referral-management': FaUserFriends,
};

// Seller menu structure
const sellerMenuConfig = [
  {
    groupTitle: 'Main',
    items: [
      {
        name: 'Dashboard',
        icon: FaTachometerAlt,
        link: '/seller/dashboard',
      },
    ],
  },
  {
    groupTitle: 'Orders',
    items: [
      {
        name: 'Orders',
        icon: FaShoppingCart,
        link: '/seller/orders',
      },
      {
        name: 'Failed Orders',
        icon: FaBan,
        link: '/seller/orders/failed',
      },
    ],
  },
  {
    groupTitle: 'Products',
    items: [
      {
        name: 'Product Research',
        icon: FaSearch,
        link: '/seller/product-research',
      },
      {
        name: 'Product Subscriptions',
        icon: FaBox,
        link: '/seller/productSubscription',
      },
      {
        name: 'Dropshipping Products',
        icon: FaBox,
        link: '/seller/dropshippingProducts',
      },
    ],
  },
  {
    groupTitle: 'Store',
    items: [
      {
        name: 'Store APIs',
        icon: FaStore,
        link: '/seller/marketplaceApis',
      },
      {
        name: 'Store Settings',
        icon: FaCog,
        link: '/seller/settings',
      },
    ],
  },
  {
    groupTitle: 'Billing',
    items: [
      {
        name: 'IM Cash',
        icon: FaWallet,
        link: '/seller/wallet',
      },
      {
        name: 'Invoices',
        icon: FaFileInvoice,
        link: '/seller/reports/invoice',
      },
    ],
  },
  {
    groupTitle: 'Account',
    items: [
      {
        name: 'Profile',
        icon: FaUser,
        link: '/seller/profile',
      },
    ],
  },
];

// Affiliate submenu (injected when user is affiliate)
const affiliatePortalChildren = [
  { name: 'Dashboard', link: '/seller/affiliate/dashboard' },
  { name: 'My Sellers', link: '/seller/affiliate/sellers' },
  { name: 'Earnings', link: '/seller/affiliate/earnings' },
  { name: 'Withdraw', link: '/seller/affiliate/withdraw' },
  { name: 'Profile', link: '/seller/affiliate/profile' },
];

export default function SellerSidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState({});
  const [userData, setUserData] = useState(null);
  const [assignedMenus, setAssignedMenus] = useState([]);

  // Load assigned menus from localStorage
  useEffect(() => {
    try {
      const storedMenus = localStorage.getItem('assignedMenus');
      if (storedMenus) {
        const parsed = JSON.parse(storedMenus);
        if (Array.isArray(parsed)) {
          setAssignedMenus(parsed);
        }
      }
    } catch (e) {
      console.error('Error parsing assignedMenus:', e);
    }
  }, []);

  // Build dynamic menu config with assigned admin menus (same structure as admin: Products > Product List / Product Drafts)
  const dynamicMenuConfig = useMemo(() => {
    const auth = getAuthLocalStorage();
    const isSeller = auth?.isSeller !== false; // default true for backward compat
    const isAffiliateOnly = auth?.isAffiliate === true && !isSeller;

    // Affiliate-only users: only Affiliate Portal menu (no Orders, Products, Store, Billing)
    if (isAffiliateOnly) {
      return [
        {
          groupTitle: 'Affiliate',
          items: [{
            name: 'Affiliate Portal',
            icon: FaUserFriends,
            children: affiliatePortalChildren,
          }],
        },
      ];
    }

    let baseConfig = [...sellerMenuConfig];

    // Filter assigned menus that have canView permission
    const viewableMenus = assignedMenus.filter(m => m.canView);
    
    if (viewableMenus.length > 0) {
      const adminItems = [];
      const productChildren = [];
      const chinaChildren = [];

      viewableMenus.forEach(menu => {
        const raw = (menu.menuLink || '').trim();
        const link = raw.startsWith('/') ? raw : `/${raw}`;
        if (
          menu.menuKey === 'product-list' ||
          menu.menuKey === 'product-drafts' ||
          menu.menuKey === 'product-errors' ||
          menu.menuKey === 'product-export'
        ) {
          productChildren.push({ name: menu.menuName, link });
        } else if (menu.menuKey === 'china-order-details' || menu.menuKey === 'postage-to-india' || menu.menuKey === 'in-comments') {
          const chinaChildNames = {
            'china-order-details': 'Order Details',
            'postage-to-india': 'Postage to India',
            'in-comments': 'IN Comments',
          };
          chinaChildren.push({
            name: chinaChildNames[menu.menuKey] || menu.menuName,
            link,
          });
        } else {
          adminItems.push({
            name: menu.menuName,
            icon: adminIconMap[menu.menuKey] || FaCog,
            link,
            menuKey: menu.menuKey,
          });
        }
      });

      // Group under "Products" (same order as admin: list, drafts, errors, export)
       const productChildOrder = (link) => {
        if (link.includes('/products/list')) return 0;
        if (link.includes('product-drafts')) return 1;
        if (link.includes('product-errors')) return 2;
        if (link.includes('product-export')) return 3;
        return 99;
      };
      productChildren.sort((a, b) => productChildOrder(a.link) - productChildOrder(b.link));

      if (productChildren.length > 0) {
        adminItems.push({
          name: 'Products',
          icon: FaBox,
          children: productChildren,
        });
      }

      if (chinaChildren.length > 0) {
        chinaChildren.sort((a, b) => a.link.localeCompare(b.link));
        adminItems.push({
          name: 'China Update',
          icon: FaGlobe,
          children: chinaChildren,
        });
      }
      
      baseConfig.push({
        groupTitle: 'Admin Access',
        items: adminItems,
      });
    }

    if (auth?.isAffiliate) {
      baseConfig.push({
        groupTitle: 'Affiliate',
        items: [{
          name: 'Affiliate Portal',
          icon: FaUserFriends,
          children: affiliatePortalChildren,
        }],
      });
    }

    return baseConfig;
  }, [assignedMenus]);

  useEffect(() => {
    const auth = getAuthLocalStorage();
    if (auth && auth.isLoggedIn) {
      const isSeller = auth.isSeller !== false;
      const displayRole = auth.isAffiliate && !isSeller ? 'Affiliate' : (auth.userType || 'seller');
      // Fetch user profile to get first name and last name
      AuthApiService.getProfile()
        .then((res) => {
          if (res?.status?.code === 200 && res?.data) {
            const firstName = res.data.firstName || '';
            const lastName = res.data.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim() || (displayRole === 'Affiliate' ? 'Affiliate' : 'Seller');
            setUserData({
              name: fullName,
              role: displayRole,
            });
          } else {
            setUserData({
              name: displayRole === 'Affiliate' ? 'Affiliate' : 'Seller',
              role: displayRole,
            });
          }
        })
        .catch(() => {
          setUserData({
            name: displayRole === 'Affiliate' ? 'Affiliate' : 'Seller',
            role: displayRole,
          });
        });
    }
  }, []);

  // Auto-expand parent menu if child is active
  useEffect(() => {
    dynamicMenuConfig.forEach(group => {
      group.items.forEach(item => {
        if (item.children) {
          const isChildActive = item.children.some(child => 
            location.pathname === child.link || location.pathname.startsWith(child.link)
          );
          if (isChildActive) {
            setOpenMenus(prev => ({ ...prev, [item.name]: true }));
          }
        }
      });
    });
  }, [location.pathname, dynamicMenuConfig]);

  const toggleMenu = (menuName) => {
    setOpenMenus(prev => {
      if (prev[menuName]) {
        return { ...prev, [menuName]: false };
      }
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      newState[menuName] = true;
      return newState;
    });
  };

  const isActive = (link, exactOnly = false) => {
    if (location.pathname === link) return true;
    if (exactOnly) return false; // For top-level items without children, only exact match
    if (location.pathname.startsWith(link + '/')) return true;
    return false;
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
  };

  const getInitials = (name) => {
    if (!name) return 'S';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <>
      <div className={`seller-sidebar-overlay ${mobileOpen ? 'active' : ''}`} onClick={closeMobileMenu} />
      
      <aside className={`seller-panel-sidebar ${collapsed ? 'seller-sidebar-collapsed' : ''} ${mobileOpen ? 'seller-sidebar-mobile-open' : ''}`}>
        {/* Mobile Close Button */}
        <button className="seller-sidebar-mobile-close" onClick={closeMobileMenu}>
          <FaTimes />
        </button>

        {/* Logo Section */}
        <div className="seller-sidebar-logo-section">
          <Link to="/" className="seller-sidebar-logo-wrapper">
            <img 
              src={collapsed ? LogoIcon : Logo} 
              alt="Logo" 
              className="seller-sidebar-logo-img" 
            />
            {/* {!collapsed && <span className="seller-sidebar-logo-text">importerr</span>} */}
          </Link>
          <button 
            className="seller-sidebar-toggle-btn" 
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>

        {/* Menu Container */}
        <nav className="seller-sidebar-menu-container">
          {dynamicMenuConfig.map((group, groupIndex) => (
            <div key={groupIndex} className="seller-menu-group">
              <div className="seller-menu-group-title">{group.groupTitle}</div>
              
              {group.items.map((item, itemIndex) => (
                <div key={itemIndex} className="seller-menu-item">
                  {item.children ? (
                    <>
                      <button
                        className={`seller-menu-item-btn seller-has-children ${item.children.some(c => isActive(c.link)) ? 'seller-menu-active' : ''} ${openMenus[item.name] ? 'seller-menu-expanded' : ''}`}
                        onClick={() => toggleMenu(item.name)}
                      >
                        <span className="seller-menu-item-icon">
                          <item.icon />
                        </span>
                        <span className="seller-menu-item-text">{item.name}</span>
                        <FaChevronDown className={`seller-menu-item-arrow ${openMenus[item.name] ? 'seller-arrow-open' : ''}`} />
                        {collapsed && <span className="seller-menu-tooltip">{item.name}</span>}
                      </button>
                      
                      <div className={`seller-submenu ${openMenus[item.name] ? 'seller-submenu-open' : ''}`}>
                        {item.children.map((child, childIndex) => (
                          <Link
                            key={childIndex}
                            to={child.link}
                            className={`seller-submenu-item ${isActive(child.link) ? 'seller-submenu-active' : ''}`}
                            onClick={closeMobileMenu}
                          >
                            <span className="seller-submenu-dot" />
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.link}
                      className={`seller-menu-item-btn ${isActive(item.link, true) ? 'seller-menu-active' : ''}`}
                      onClick={closeMobileMenu}
                    >
                      <span className="seller-menu-item-icon">
                        <item.icon />
                      </span>
                      <span className="seller-menu-item-text">{item.name}</span>
                      {collapsed && <span className="seller-menu-tooltip">{item.name}</span>}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          ))}
        </nav>

        {/* User Section */}
        <div className="seller-sidebar-user-section">
          <div className="seller-sidebar-user-card" onClick={handleLogout}>
            <div className="seller-sidebar-user-avatar">
              {getInitials(userData?.name)}
            </div>
            <div className="seller-sidebar-user-info">
              <div className="seller-sidebar-user-name">{userData?.name}</div>
              <div className="seller-sidebar-user-role">{userData?.role}</div>
            </div>
            <FaSignOutAlt style={{ color: 'var(--seller-sidebar-text-muted)' }} />
          </div>
        </div>
      </aside>
    </>
  );
}
